import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmtDate } from "@/utils/format";
import { toast } from "sonner";
import { Plus, Trash2, Search, ChevronLeft, ChevronRight, FileText, ShoppingBag } from "lucide-react";

const TUR_CFG = {
  satis: { label: "Satış", color: "bg-blue-100 text-blue-700 border-blue-200" },
  satin_alma: { label: "Satın Alma", color: "bg-purple-100 text-purple-700 border-purple-200" },
};

const EMPTY_FORM = {
  fatura_no: "", tarih: new Date().toISOString().split("T")[0],
  tur: "satis", cari_unvan: "", ara_toplam: 0, kdv: 0, toplam: 0, aciklama: "",
};

export default function Faturalar() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [turFilter, setTurFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (turFilter) params.tur = turFilter;
      if (search) params.search = search;
      const [listRes, sumRes] = await Promise.all([
        api.get("/faturalar/", { params }),
        api.get("/faturalar/summary"),
      ]);
      setItems(listRes.data.items);
      setTotal(listRes.data.total);
      setPages(listRes.data.pages || 1);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, turFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [turFilter, search]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/faturalar/", form);
      toast.success("Fatura oluşturuldu");
      setShowModal(false);
      setForm(EMPTY_FORM);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu faturayı silmek istediğinizden emin misiniz?")) return;
    try {
      await api.delete(`/faturalar/${id}`);
      toast.success("Fatura silindi");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const satis = summary.satis || { count: 0, toplam: 0 };
  const satinAlma = summary.satin_alma || { count: 0, toplam: 0 };

  const F = ({ label, name, type = "text", required }) => (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
      <input type={type} value={form[name] ?? ""} required={required}
        step={type === "number" ? "0.01" : undefined}
        onChange={(e) => setForm((p) => ({ ...p, [name]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors" />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>Faturalar</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} kayıt</p>
        </div>
        <button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#0D1B3E] text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#172A58] active:scale-[0.98] transition-all">
          <Plus size={15} /> Yeni Fatura
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: "Toplam Satış", data: satis, icon: ShoppingBag, color: "bg-blue-600" },
          { label: "Toplam Satın Alma", data: satinAlma, icon: FileText, color: "bg-purple-600" },
        ].map(({ label, data, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
              <Icon size={18} className="text-white" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
              <div className="text-xl font-bold tabular-nums text-slate-800 mt-0.5">{fmtCurrency(data.toplam)}</div>
              <div className="text-xs text-slate-400">{data.count} fatura</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Fatura no veya cari ara..."
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors" />
        </div>
        <div className="flex gap-2">
          {[{ v: "", l: "Tümü" }, { v: "satis", l: "Satış" }, { v: "satin_alma", l: "Satın Alma" }].map(({ v, l }) => (
            <button key={v} onClick={() => setTurFilter(v)}
              className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${turFilter === v ? "bg-[#0D1B3E] text-white border-[#0D1B3E]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="table-container">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr>
                {["Fatura No", "Tarih", "Tür", "Cari", "Ara Toplam", "KDV", "Toplam", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Kayıt bulunamadı</td></tr>
              ) : items.map((item) => {
                const cfg = TUR_CFG[item.tur] || { label: item.tur, color: "bg-slate-100 text-slate-600 border-slate-200" };
                return (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-sm font-medium text-[#0D1B3E]">{item.fatura_no}</td>
                    <td className="px-4 py-3 tabular-nums text-xs text-slate-500">{fmtDate(item.tarih)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${cfg.color}`}>{cfg.label}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{item.cari_unvan || "-"}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-600">{fmtCurrency(item.ara_toplam)}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-600">{fmtCurrency(item.kdv)}</td>
                    <td className="px-4 py-3 tabular-nums font-bold text-slate-800">{fmtCurrency(item.toplam)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <span className="text-xs text-slate-500">{total} kayıt</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40"><ChevronLeft size={15} /></button>
            <span className="text-xs text-slate-600 px-2">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40"><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-bold text-[#0D1B3E]">Yeni Fatura</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <F label="Fatura No *" name="fatura_no" required />
                <F label="Tarih *" name="tarih" type="date" required />
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Tür *</label>
                  <select value={form.tur} onChange={(e) => setForm((p) => ({ ...p, tur: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20">
                    <option value="satis">Satış</option>
                    <option value="satin_alma">Satın Alma</option>
                  </select>
                </div>
                <F label="Cari Ünvan" name="cari_unvan" />
                <F label="Ara Toplam" name="ara_toplam" type="number" />
                <F label="KDV" name="kdv" type="number" />
                <F label="Toplam *" name="toplam" type="number" required />
              </div>
              <F label="Açıklama" name="aciklama" />
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-md bg-[#0D1B3E] text-white hover:bg-[#172A58] disabled:opacity-60 transition-colors">
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
